/* eslint-disable @next/next/no-img-element */
'use client'

import { useState } from 'react'
import type { FipavStanding } from '@/lib/fipav/scraper'
import type { PoolMatch } from '@/lib/services/competitions-hub'

interface StandingsFullTableProps {
  standings: FipavStanding[]
  allMatches: PoolMatch[]
  limit?: number
  collapsible?: boolean
  /** Nomi delle squadre da evidenziare (es. i due contendenti di un match) */
  highlightTeams?: string[]
  /**
   * Squadra selezionata dall'utente. Se `onSelectTeam` è presente le righe
   * diventano cliccabili e questa è quella attiva.
   */
  selectedTeam?: string | null
  /** Chiamata al click su una riga; `null` quando si deseleziona. */
  onSelectTeam?: (team: string | null) => void
}

function PositionBadge({ position }: { position: number }) {
  const base = 'flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px] sm:h-6 sm:w-6 sm:text-[12px]'
  if (position === 1) {
    return <span className={`${base} bg-[color:var(--league-brand-solid)] font-bold text-white`}>{position}</span>
  }
  if (position <= 3) {
    return (
      <span className={`${base} bg-[color:var(--league-surface-alt)] font-bold text-[color:var(--league-brand)]`}>
        {position}
      </span>
    )
  }
  return <span className={`${base} font-medium text-[color:var(--league-muted)]`}>{position}</span>
}

function logoProxyUrl(src: string) {
  return `/api/logo?src=${encodeURIComponent(src)}`
}

function TeamAvatar({ logoSrc, team }: { logoSrc: string | null; team: string }) {
  if (logoSrc) {
    return (
      <img
        src={logoProxyUrl(logoSrc)}
        alt=""
        width={24}
        height={24}
        loading="lazy"
        decoding="async"
        className="h-5 w-5 shrink-0 rounded-full object-contain sm:h-6 sm:w-6"
      />
    )
  }
  return null
}

function sameTeam(a: string, b: string) {
  return a.toLowerCase() === b.toLowerCase()
}

function computeTeamRecord(teamName: string, allMatches: PoolMatch[]): { wins: number; losses: number; setsWon: number; setsLost: number } {
  let wins = 0
  let losses = 0
  let setsWon = 0
  let setsLost = 0
  for (const m of allMatches) {
    if (!m.played || !m.sets) continue
    // sets è una stringa tipo "25-20 25-22 25-23"
    const setPairs = m.sets.match(/\d{1,2}-\d{1,2}/g)
    if (!setPairs || setPairs.length === 0) continue

    const isHome = sameTeam(m.home, teamName)
    const isAway = sameTeam(m.away, teamName)
    if (!isHome && !isAway) continue

    let teamSetCount = 0
    let oppSetCount = 0
    for (const pair of setPairs) {
      const parts = pair.split('-').map(Number)
      if (parts.length !== 2 || isNaN(parts[0]) || isNaN(parts[1])) continue
      if (isHome) {
        if (parts[0] > parts[1]) teamSetCount++
        else oppSetCount++
      } else {
        if (parts[1] > parts[0]) teamSetCount++
        else oppSetCount++
      }
    }
    setsWon += teamSetCount
    setsLost += oppSetCount
    if (teamSetCount > oppSetCount) wins++
    else losses++
  }
  return { wins, losses, setsWon, setsLost }
}

/**
 * Classifica del girone.
 *
 * Larghezza: la tabella **non ha una larghezza minima** e non deve mai produrre
 * scorrimento orizzontale dentro la card. Il nome squadra è la colonna elastica
 * (`flex-1 truncate`): assorbe lo spazio che avanza e si tronca quando manca.
 *
 * Sotto `sm` la card è larga ~360px: dieci colonne con numeri leggibili e un
 * nome squadra utilizzabile non ci stanno, quindi dal basso restano solo
 * `PG V P Pt` (il minimo per leggere la classifica) e le quattro colonne di
 * dettaglio (`SV SP PF PS`) compaiono da `sm` in su.
 *
 * Selezione: se arriva `onSelectTeam`, ogni riga è un `<button>` che seleziona
 * la squadra (secondo click = deseleziona). Serve al match center per
 * confrontare una terza squadra nel grafico dell'andamento.
 */
export function StandingsFullTable({
  standings,
  allMatches,
  limit = 3,
  collapsible = false,
  highlightTeams,
  selectedTeam = null,
  onSelectTeam,
}: StandingsFullTableProps) {
  const [expanded, setExpanded] = useState(false)

  if (standings.length === 0) {
    return <div className="text-[14px] leading-6 text-[color:var(--league-muted)]">Classifica in aggiornamento.</div>
  }

  const effectiveLimit = collapsible && !expanded ? limit : undefined
  const rows = effectiveLimit ? standings.slice(0, effectiveLimit) : standings
  const hasMore = collapsible && standings.length > limit

  return (
    <div className="min-w-0 space-y-0.5">
      {/* Header */}
      <div className="flex items-center gap-1 px-1.5 py-1.5 text-[11px] font-semibold uppercase tracking-[0.1em] text-[color:var(--league-muted)] sm:gap-2 sm:px-2 sm:text-[12px] sm:tracking-[0.16em]">
        <span className="w-5 shrink-0 text-center sm:w-6">#</span>
        <span className="min-w-0 flex-1">Squadra</span>
        <span className="w-6 shrink-0 text-center sm:w-8">PG</span>
        <span className="w-6 shrink-0 text-center sm:w-8">V</span>
        <span className="w-6 shrink-0 text-center sm:w-8">P</span>
        <span className="hidden w-8 shrink-0 text-center sm:block">SV</span>
        <span className="hidden w-8 shrink-0 text-center sm:block">SP</span>
        <span className="hidden w-10 shrink-0 text-center sm:block">PF</span>
        <span className="hidden w-10 shrink-0 text-center sm:block">PS</span>
        <span className="w-7 shrink-0 text-right sm:w-10">Pt</span>
      </div>
      {rows.map((team) => {
        const record = computeTeamRecord(team.team, allMatches)
        const hasRecord = record.wins + record.losses > 0
        const highlighted = highlightTeams?.some((t) => sameTeam(team.team, t) || t.toLowerCase().includes(team.team.toLowerCase()))
        const selected = selectedTeam != null && sameTeam(selectedTeam, team.team)

        const cells = (
          <>
            <PositionBadge position={team.position} />
            <TeamAvatar logoSrc={team.logoSrc} team={team.team} />
            <span className="min-w-0 flex-1 truncate font-medium text-[color:var(--league-ink)]">{team.team}</span>
            <span className="w-6 shrink-0 text-center text-[13px] tabular-nums text-[color:var(--league-muted)] sm:w-8 sm:text-[14px]">{team.played}</span>
            <span className="w-6 shrink-0 text-center text-[13px] tabular-nums text-[color:var(--league-muted)] sm:w-8 sm:text-[14px]">{hasRecord ? record.wins : '-'}</span>
            <span className="w-6 shrink-0 text-center text-[13px] tabular-nums text-[color:var(--league-muted)] sm:w-8 sm:text-[14px]">{hasRecord ? record.losses : '-'}</span>
            <span className="hidden w-8 shrink-0 text-center text-[14px] tabular-nums text-[color:var(--league-muted)] sm:block">{hasRecord ? record.setsWon : '-'}</span>
            <span className="hidden w-8 shrink-0 text-center text-[14px] tabular-nums text-[color:var(--league-muted)] sm:block">{hasRecord ? record.setsLost : '-'}</span>
            <span className="hidden w-10 shrink-0 text-center text-[14px] tabular-nums text-[color:var(--league-muted)] sm:block">{team.pointsFor ?? '-'}</span>
            <span className="hidden w-10 shrink-0 text-center text-[14px] tabular-nums text-[color:var(--league-muted)] sm:block">{team.pointsAgainst ?? '-'}</span>
            <span className="w-7 shrink-0 text-right text-[13px] font-bold tabular-nums text-[color:var(--league-ink)] sm:w-10 sm:text-[14px]">{team.points}</span>
          </>
        )

        const rowClass = `flex w-full items-center gap-1 rounded-lg px-1.5 py-1.5 text-left text-[13px] transition-colors sm:gap-2 sm:px-2 sm:text-[14px]${
          onSelectTeam ? ' cursor-pointer hover:bg-[color:var(--league-surface-alt)]' : ''
        }`
        // Selezione > contendenti: la riga scelta comanda, perché è quella che
        // l'utente ha appena indicato.
        const rowStyle = {
          background: selected
            ? 'var(--league-accent-soft)'
            : highlighted
              ? 'rgba(255,200,0,0.20)'
              : 'transparent',
          borderLeft: selected
            ? '3px solid var(--league-brand)'
            : highlighted
              ? '3px solid #e6b800'
              : '2px solid transparent',
        }

        if (!onSelectTeam) {
          return (
            <div key={`${team.position}-${team.team}`} className={rowClass} style={rowStyle}>
              {cells}
            </div>
          )
        }

        return (
          <button
            key={`${team.position}-${team.team}`}
            type="button"
            onClick={() => onSelectTeam(selected ? null : team.team)}
            aria-pressed={selected}
            className={rowClass}
            style={rowStyle}
          >
            {cells}
          </button>
        )
      })}
      {hasMore && !expanded && (
        <button
          onClick={() => setExpanded(true)}
          className="w-full px-3 pt-2 text-[12px] font-medium text-[color:var(--league-brand)] transition-colors hover:text-[color:var(--league-brand-vivid)]"
        >
          Mostra tutto ({standings.length} squadre) ↓
        </button>
      )}
      {expanded && (
        <button
          onClick={() => setExpanded(false)}
          className="w-full px-3 pt-2 text-[12px] font-medium text-[color:var(--league-muted)] transition-colors hover:text-[color:var(--league-ink)]"
        >
          Nascondi ↑
        </button>
      )}
    </div>
  )
}
